import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import CollectionsBookmarkRoundedIcon from '@mui/icons-material/CollectionsBookmarkRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import { useAuth } from '../context/AuthContext';
import { useVisitor } from '../context/VisitorContext';
import { levelForXp, tierForLevel, xpProgressInLevel } from '../utils/levels';
import './game/game.css';

const Navbar = ({ hideLogin = false }) => {
  const { currentUser, logout } = useAuth();
  const { profile: visitorProfile } = useVisitor();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const visitorLevel = visitorProfile ? levelForXp(visitorProfile.xp || 0) : null;
  const visitorTier = visitorLevel ? tierForLevel(visitorLevel) : null;
  const visitorXpProgress = visitorProfile ? xpProgressInLevel(visitorProfile.xp || 0) : null;

  const userName = useMemo(() => {
    if (!currentUser) return '';
    return currentUser.displayName || currentUser.email || 'Usuario';
  }, [currentUser]);

  const userInitials = useMemo(() => {
    if (!userName) return 'U';
    const parts = userName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }, [userName]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Failed to log out", error);
    }
  }, [logout, navigate]);

  const navItems = useMemo(() => {
    if (currentUser) {
      return [
        { label: 'Escenas Geo', to: '/geoscenes', icon: <PublicRoundedIcon fontSize="small" /> },
        { label: 'Museo Virtual', to: '/coleccion', icon: <CollectionsBookmarkRoundedIcon fontSize="small" /> },
        { label: 'Panel Admin', to: '/admin/dashboard', icon: <DashboardRoundedIcon fontSize="small" /> },
        { label: 'Geo Admin', to: '/admin/geoscenes', icon: <TravelExploreRoundedIcon fontSize="small" /> },
        { label: 'Eventos', to: '/admin/events', icon: <EventRoundedIcon fontSize="small" /> },
        { label: 'Cerrar Sesión', action: handleLogout, icon: <LogoutRoundedIcon fontSize="small" />, danger: true }
      ];
    }
    return [
      { label: 'Escenas Geo', to: '/geoscenes', icon: <PublicRoundedIcon fontSize="small" /> },
      { label: 'Museo Virtual', to: '/coleccion', icon: <CollectionsBookmarkRoundedIcon fontSize="small" /> },
      { label: 'Iniciar Sesión', to: '/login', icon: <DashboardRoundedIcon fontSize="small" /> }
    ];
  }, [currentUser, handleLogout]);

  const handleItemClick = async (item) => {
    setMobileMenuOpen(false);
    if (item.action) {
      await item.action();
      return;
    }
    if (item.to) navigate(item.to);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        top: 0,
        zIndex: 1200,
        borderBottom: '1px solid rgba(109, 232, 255, 0.2)',
        background:
          'linear-gradient(112deg, rgba(4,10,27,0.95), rgba(8,14,35,0.93) 42%, rgba(12,20,48,0.95))',
        backdropFilter: 'blur(12px)'
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 68, md: 74 }, px: { xs: 1.2, md: 2.4 }, gap: 1.2 }}>
        <Box component={Link} to="/" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.2, textDecoration: 'none' }}>
          <Avatar
            src="/logo128.png"
            alt="Logo"
            variant="rounded"
            sx={{
              width: 42,
              height: 42,
              bgcolor: '#0d1631',
              border: '1px solid rgba(127, 245, 255, 0.4)',
              p: 0.55
            }}
          />
          <Box>
            <Typography sx={{ color: '#eaf7ff', fontWeight: 700, lineHeight: 1.1, fontSize: { xs: '0.96rem', sm: '1.05rem' } }}>
              Museo Digital 3D
            </Typography>
            <Typography sx={{ color: '#88a8cb', fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Plataforma AR
            </Typography>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {!hideLogin && visitorProfile && (
          <Tooltip title={`${visitorTier?.name || ''} · Nivel ${visitorLevel} · ${visitorProfile.xp || 0} XP`}>
            <Box component={Link} to="/coleccion" className="level-badge" sx={{ mr: 1 }}>
              <span className="level-badge-num">{visitorLevel}</span>
              {visitorTier?.name}
              {visitorXpProgress && (
                <span className="level-badge-bar">
                  <span className="level-badge-bar-fill" style={{ width: `${visitorXpProgress.percent}%` }} />
                </span>
              )}
            </Box>
          </Tooltip>
        )}

        {!hideLogin && (
          <>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              {navItems.filter((item) => item.to).map((item) => (
                <Button
                  key={item.label}
                  component={Link}
                  to={item.to}
                  startIcon={item.icon}
                  sx={{
                    color: '#d7e8ff',
                    border: '1px solid rgba(138, 182, 255, 0.24)',
                    borderRadius: '12px',
                    px: 1.2,
                    py: 0.68,
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    textTransform: 'none',
                    background: 'rgba(10,18,43,0.55)',
                    '&:hover': {
                      borderColor: 'rgba(121, 237, 255, 0.62)',
                      background: 'rgba(13,26,58,0.9)'
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}

              {currentUser && (
                <Tooltip title={currentUser.email || 'Usuario autenticado'}>
                  <Box
                    sx={{
                      ml: 0.6,
                      px: 0.8,
                      py: 0.45,
                      borderRadius: '999px',
                      border: '1px solid rgba(136, 242, 255, 0.42)',
                      background: 'rgba(9, 22, 53, 0.78)',
                      color: '#cceeff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      maxWidth: 260
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 27,
                        height: 27,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        bgcolor: 'rgba(95, 235, 255, 0.22)',
                        color: '#eaffff',
                        border: '1px solid rgba(126, 242, 255, 0.38)'
                      }}
                    >
                      {userInitials}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.76rem', lineHeight: 1.1, color: '#e6f7ff', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {userName}
                      </Typography>
                      <Typography sx={{ fontSize: '0.66rem', color: '#97b9de', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {currentUser.email}
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>
              )}

              {currentUser && (
                <Button
                  onClick={handleLogout}
                  startIcon={<LogoutRoundedIcon fontSize="small" />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    color: '#ffd7df',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 123, 152, 0.42)',
                    background: 'rgba(56,16,33,0.52)',
                    '&:hover': { background: 'rgba(72,20,41,0.8)' }
                  }}
                >
                  Salir
                </Button>
              )}
            </Box>

            <IconButton
              color="inherit"
              edge="end"
              aria-label="Abrir menú"
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                border: '1px solid rgba(130, 238, 255, 0.4)',
                background: 'rgba(7, 16, 41, 0.75)',
                color: '#d7f7ff',
                borderRadius: '10px'
              }}
            >
              <MenuIcon />
            </IconButton>

            <Drawer
              anchor="right"
              open={mobileMenuOpen}
              onClose={() => setMobileMenuOpen(false)}
              PaperProps={{
                sx: {
                  width: 'min(84vw, 340px)',
                  background: 'linear-gradient(145deg, #070d22, #0a1430)',
                  color: '#d7e9ff'
                }
              }}
            >
              <Box sx={{ p: 2.2, borderBottom: '1px solid rgba(133, 201, 255, 0.25)' }}>
                <Typography sx={{ fontWeight: 700, color: '#ebf9ff', mb: 0.2 }}>Navegación</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#94b6da' }}>Museo Digital 3D</Typography>
              </Box>

              {currentUser && (
                <Box sx={{ px: 2, py: 1.4, display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      bgcolor: 'rgba(92, 240, 255, 0.22)',
                      color: '#ecfdff',
                      border: '1px solid rgba(127, 239, 255, 0.4)'
                    }}
                  >
                    {userInitials}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#effbff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {userName}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#97b6d9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {currentUser.email}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Divider sx={{ borderColor: 'rgba(126, 173, 238, 0.2)' }} />

              <List sx={{ pt: 0.5 }}>
                {navItems.map((item) => (
                  <ListItemButton
                    key={item.label}
                    onClick={() => handleItemClick(item)}
                    sx={{
                      mx: 1,
                      my: 0.3,
                      borderRadius: '10px',
                      color: item.danger ? '#ffd2db' : '#d6e9ff',
                      '&:hover': {
                        background: item.danger ? 'rgba(94, 23, 44, 0.42)' : 'rgba(45, 94, 170, 0.22)'
                      }
                    }}
                  >
                    <Box sx={{ mr: 1, display: 'inline-flex', alignItems: 'center', opacity: 0.9 }}>{item.icon}</Box>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 600 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Drawer>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
